/**
 * Notification Toast Component
 * Displays toast notifications for achievements, streaks, and activities
 */

import React, { useEffect } from 'react';

const Toast = ({ notification, onClose }) => {
  const {
    id,
    type = 'success',
    title = 'Notification',
    message = '',
    duration = 5000,
    action = null,
    actionText = 'View'
  } = notification;

  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const colorMap = {
    achievement: { bg: '#fff3cd', border: '#ffc107', text: '#856404' },
    streak: { bg: '#d4edda', border: '#28a745', text: '#155724' },
    success: { bg: '#d4edda', border: '#28a745', text: '#155724' },
    info: { bg: '#d1ecf1', border: '#17a2b8', text: '#0c5460' },
    warning: { bg: '#fff3cd', border: '#ffc107', text: '#856404' },
    error: { bg: '#f8d7da', border: '#f5c6cb', text: '#721c24' }
  };

  const colors = colorMap[type] || colorMap.success;

  const styles = {
    container: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      maxWidth: '400px',
      padding: '16px',
      background: colors.bg,
      border: `2px solid ${colors.border}`,
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 9999,
      animation: 'slideIn 0.3s ease-out',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'start',
      gap: '12px'
    },
    content: {
      flex: 1
    },
    title: {
      fontSize: '1rem',
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: '4px'
    },
    message: {
      fontSize: '0.9rem',
      color: colors.text,
      marginBottom: action ? '8px' : '0'
    },
    actionButton: {
      background: 'transparent',
      border: `1px solid ${colors.text}`,
      color: colors.text,
      padding: '4px 12px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    closeButton: {
      background: 'transparent',
      border: 'none',
      color: colors.text,
      fontSize: '1.5rem',
      cursor: 'pointer',
      padding: '0',
      lineHeight: '1'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.title}>{title}</div>
        <div style={styles.message}>{message}</div>
        {action && (
          <button
            style={styles.actionButton}
            onClick={() => {
              action();
              onClose();
            }}
            onMouseEnter={(e) => e.target.style.background = colors.border}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            {actionText}
          </button>
        )}
      </div>
      <button style={styles.closeButton} onClick={onClose}>
        ×
      </button>
    </div>
  );
};

/**
 * Toast Container Component - Manages multiple toast notifications
 */
export const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(400px);
            opacity: 0;
          }
        }
      `}</style>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          notification={toast}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

export default Toast;
